import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { ProjectService } from '../../services/project.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { getProjectIconDef } from '../../utils/project-icon.utils';
import { findInRouteTree, findRouteParam } from '../../shared/route-params';

@Component({
  selector: 'app-project-nav',
  imports: [RouterLink, TranslatePipe, MatIcon],
  templateUrl: './project-nav.component.html',
  styleUrl: './project-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectNavComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private projectService = inject(ProjectService);
  private destroyRef = inject(DestroyRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private projectRole = inject(ProjectRoleService, { optional: true });

  readonly projectId: string;
  readonly projectName = signal<string>('');
  readonly projectIconMat = signal<string>('folder');
  readonly projectIconColor = signal<string>('var(--to-project-icon-folder)');
  readonly canManageProject = computed(() => this.projectRole?.canManageProject() ?? false);

  constructor() {
    this.projectId = this.findParam('projectId') ?? '';
    const resolvedName = this.findResolvedName();
    if (resolvedName) this.projectName.set(resolvedName);
    afterNextRender(() => this.keepActiveTabVisible());
  }

  ngOnInit(): void {
    if (!this.projectId) return;
    this.projectService
      .getById(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (project) => {
          if (!this.projectName()) this.projectName.set(project.name);
          const def = getProjectIconDef(project);
          this.projectIconMat.set(def.matIcon);
          this.projectIconColor.set(def.color);
        },
        error: (err) => console.error('ProjectNavComponent: failed to load project', err),
      });
  }

  private findResolvedName(): string {
    return (
      findInRouteTree(this.route, (snapshot) => {
        if (snapshot.routeConfig?.path !== ':projectId') return null;
        const value = snapshot.data?.['breadcrumb'];
        return typeof value === 'string' && value.length > 0 ? value : null;
      }) ?? ''
    );
  }

  // Beim Neuladen kommen Beschriftungen und Schrift erst nach dem ersten Rendern an,
  // der aktive Reiter wird dann breiter — einmal messen reicht deshalb nicht.
  private keepActiveTabVisible(): void {
    const active = this.host.nativeElement.querySelector<HTMLElement>('.project-nav__tab--active');
    if (!active) return;
    const observer = new ResizeObserver(() => this.reveal(active));
    observer.observe(active);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  // Bewusst nicht `scrollIntoView`: das scrollt auch die Seite, sobald die Leiste
  // senkrecht ausserhalb des Fensters liegt — der Router behaelt die Scrollposition.
  private reveal(tab: HTMLElement): void {
    const nav = tab.closest<HTMLElement>('.project-nav');
    if (!nav) return;
    const visibleRight =
      nav.getBoundingClientRect().left +
      nav.clientLeft +
      nav.clientWidth -
      parseFloat(getComputedStyle(nav).paddingRight);
    const hidden = tab.getBoundingClientRect().right - visibleRight;
    if (hidden > 0) nav.scrollLeft += hidden;
  }

  openSettings(): void {
    this.router.navigate(['/projects', this.projectId, 'edit']);
  }

  isActive(section: string): boolean {
    return this.router.url.includes(`/${section}`);
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}
